import {
  scopeKey,
  type ReconciliationScope,
} from '@api-types/sse/reconciliation-scope';

/**
 * Per-`(consensusColumnId, dateValue)` map of reconciliation scopes. The
 * nested shape lets the multi-month grid scope spinners per `(column,
 * month)` so concurrent edits on the same column for different months
 * don't share state. Multiple concurrent jobs on the same key produce
 * multiple scopes; a row is considered "in any of them" iff at least
 * one scope matches it.
 */
export type ScopeMap = ReadonlyMap<
  string,
  ReadonlyMap<string, readonly ReconciliationScope[]>
>;

export function upsertScope(
  current: ScopeMap,
  columnId: string,
  dateValue: string,
  scope: ReconciliationScope,
): ScopeMap {
  const key = scopeKey(scope);
  const byMonth = new Map(current.get(columnId) ?? []);
  const existing = byMonth.get(dateValue) ?? [];

  if (existing.some((s) => scopeKey(s) === key)) {
    return current;
  }

  byMonth.set(dateValue, [...existing, scope]);
  const next = new Map(current);
  next.set(columnId, byMonth);

  return next;
}

export function removeScope(
  current: ScopeMap,
  columnId: string,
  dateValue: string,
  scope: ReconciliationScope,
): ScopeMap {
  const byColumn = current.get(columnId);

  if (!byColumn) return current;

  const list = byColumn.get(dateValue);

  if (!list) return current;

  const key = scopeKey(scope);
  const remaining = list.filter((s) => scopeKey(s) !== key);
  if (remaining.length === list.length) {
    return current;
  }

  const byMonth = new Map(byColumn);
  if (remaining.length === 0) {
    byMonth.delete(dateValue);
  } else {
    byMonth.set(dateValue, remaining);
  }

  const next = new Map(current);
  if (byMonth.size === 0) {
    next.delete(columnId);
  } else {
    next.set(columnId, byMonth);
  }
  return next;
}

export function buildScopeMap(
  snapshot: Record<string, Record<string, readonly ReconciliationScope[]>>,
): ScopeMap {
  const next = new Map<
    string,
    ReadonlyMap<string, readonly ReconciliationScope[]>
  >();
  for (const [columnId, byMonth] of Object.entries(snapshot)) {
    const monthMap = new Map<string, readonly ReconciliationScope[]>();
    for (const [dateValue, scopes] of Object.entries(byMonth)) {
      if (scopes.length > 0) {
        monthMap.set(dateValue, [...scopes]);
      }
    }
    if (monthMap.size > 0) {
      next.set(columnId, monthMap);
    }
  }
  return next;
}

export function extractScopes(map: ScopeMap): ReconciliationScope[] {
  const out: ReconciliationScope[] = [];
  for (const byMonth of map.values()) {
    for (const scopes of byMonth.values()) {
      out.push(...scopes);
    }
  }
  return out;
}
